import PropTypes from 'prop-types';
import { createComponent } from '../core/create-component.js';

const OneThroughTwelve = [
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12
];
const ZeroThroughTwelve = [
    0,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12
];
const propTypes = {
    /**
   * The number of columns that this Column should occupy
   */ span: PropTypes.oneOf(OneThroughTwelve).isRequired,
    /**
   * The number of columns that this Column should occupy on extra-small screens
   */ spanXs: PropTypes.oneOf(OneThroughTwelve),
    /**
   * The number of columns that this Column should occupy on small screens
   */ spanSm: PropTypes.oneOf(OneThroughTwelve),
    /**
   * The number of columns that this Column should occupy on medium screens
   */ spanMd: PropTypes.oneOf(OneThroughTwelve),
    /**
   * The number of columns that this Column should occupy on large screens
   */ spanLg: PropTypes.oneOf(OneThroughTwelve),
    /**
   * The number of columns that this Column should occupy on X-large screens
   */ spanXl: PropTypes.oneOf(OneThroughTwelve),
    /**
   * The default number of columns that this column should be shifted to the right
   */ offset: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * The number of columns that this column should be shifted to the right on small screens
   */ offsetSm: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * The number of columns that this column should be shifted to the right on medium screens
   */ offsetMd: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * The number of columns that this column should be shifted to the right on large screens
   */ offsetLg: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * The number of columns that this column should be shifted to the right on X-large screens
   */ offsetXl: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order by pushing it to the right by a specified number of columns.
   */ push: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order on small screens by pushing it to the right by a specified number of columns.
   */ pushSm: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order on medium screens by pushing it to the right by a specified number of columns.
   */ pushMd: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order on large screens by pushing it to the right by a specified number of columns.
   */ pushLg: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order on X-large screens by pushing it to the right by a specified number of columns.
   */ pushXl: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order by pulling it to the left by a specified number of columns.
   */ pull: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order on small screens by pulling it to the left by a specified number of columns.
   */ pullSm: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order on medium screens by pulling it to the left by a specified number of columns.
   */ pullMd: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order on large screens by pulling it to the left by a specified number of columns.
   */ pullLg: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * Change the column order on X-large screens by pulling it to the left by a specified number of columns.
   */ pullXl: PropTypes.oneOf(ZeroThroughTwelve),
    /**
   * In a lineWrap GridContainer, decide whether to arrange the Column's children as a single column, evenly spaced between the top and bottom.
   */ flexColumn: PropTypes.bool,
    /**
   * Whether or not the Column's content should be vertically centered within the Row
   */ verticallyCenterContent: PropTypes.bool,
    /**
   *  Whether or not the Column's content should be sticky
   */ sticky: PropTypes.bool,
    /**
   * If a Column is normally sticky, which screen sizes the Column should NOT be sticky on
   * Available options: "none", "xs"
   */ unsticky: PropTypes.oneOf([
        'none',
        'xs'
    ]),
    /**
   * Whether the Column is a Floating Container
   */ floatingContainer: PropTypes.bool,
    /**
   * Alignment for floating Container
   */ floatingContainerAlignment: PropTypes.oneOf([
        'left',
        'right'
    ])
};
/**
 * @subcomponent Grid
 */ const Column = createComponent({
    displayName: 'Column',
    root: 'div',
    propTypes,
    propConfig: {
        classNames (props) {
            const { span, spanXs, spanSm, spanMd, spanLg, spanXl, offset, offsetSm, offsetMd, offsetLg, offsetXl, push, pushSm, pushMd, pushLg, pushXl, pull, pullSm, pullMd, pullLg, pullXl, flexColumn = false, verticallyCenterContent = false, sticky = false, unsticky = 'none', floatingContainer = false, floatingContainerAlignment = 'left' } = props;
            const classNameSet = new Set([
                `swan-col-${span}`
            ]);
            if (spanXs !== undefined) classNameSet.add(`swan-col-xs-${spanXs}`);
            if (spanSm !== undefined) classNameSet.add(`swan-col-sm-${spanSm}`);
            if (spanMd !== undefined) classNameSet.add(`swan-col-md-${spanMd}`);
            if (spanLg !== undefined) classNameSet.add(`swan-col-lg-${spanLg}`);
            if (spanXl !== undefined) classNameSet.add(`swan-col-xl-${spanXl}`);
            if (offset !== undefined) classNameSet.add(`swan-col-offset-${offset}`);
            if (offsetSm !== undefined) classNameSet.add(`swan-col-sm-offset-${offsetSm}`);
            if (offsetMd !== undefined) classNameSet.add(`swan-col-md-offset-${offsetMd}`);
            if (offsetLg !== undefined) classNameSet.add(`swan-col-lg-offset-${offsetLg}`);
            if (offsetXl !== undefined) classNameSet.add(`swan-col-xl-offset-${offsetXl}`);
            if (pull !== undefined) classNameSet.add(`swan-col-pull-${pull}`);
            if (pullSm !== undefined) classNameSet.add(`swan-col-sm-pull-${pullSm}`);
            if (pullMd !== undefined) classNameSet.add(`swan-col-md-pull-${pullMd}`);
            if (pullLg !== undefined) classNameSet.add(`swan-col-lg-pull-${pullLg}`);
            if (pullXl !== undefined) classNameSet.add(`swan-col-xl-pull-${pullXl}`);
            if (push !== undefined) classNameSet.add(`swan-col-push-${push}`);
            if (pushSm !== undefined) classNameSet.add(`swan-col-sm-push-${pushSm}`);
            if (pushMd !== undefined) classNameSet.add(`swan-col-md-push-${pushMd}`);
            if (pushLg !== undefined) classNameSet.add(`swan-col-lg-push-${pushLg}`);
            if (pushXl !== undefined) classNameSet.add(`swan-col-xl-push-${pushXl}`);
            if (flexColumn) classNameSet.add('swan-col-flex-column');
            if (verticallyCenterContent) classNameSet.add('swan-col-vertically-center');
            if (sticky) classNameSet.add('swan-col-sticky');
            if (unsticky === 'xs') classNameSet.add('swan-col-unsticky-xs');
            if (floatingContainer) classNameSet.add('swan-floating-container');
            if (floatingContainerAlignment === 'right') classNameSet.add('swan-floating-container-right');
            return classNameSet;
        }
    }
});
Column.defaultProps = {
    flexColumn: false,
    verticallyCenterContent: false,
    sticky: false,
    unsticky: 'none',
    floatingContainer: false,
    floatingContainerAlignment: 'left'
};

export { Column };
